import { addMonths, parse } from "date-fns";
import { useCallback, useMemo } from "react";
import { MAX_BOOKING_MONTHS_IN_ADVANCE } from "../../../core/availability";
import useAvailability from "../../../hooks/useAvailability";
import InstantBooking from "../../Atoms/InstantBooking";
import * as TimePicker from "../../Atoms/TimePicker";
import Toggle from "../../Atoms/Toggle";
import ToolTip from "../../Atoms/ToolTip";
import { Text } from "../../Atoms/Typography";
import LoadingCalendar from "./LoadingCalendar";

function stringToDate(dateString) {
  return parse(dateString, "yyyy-MM-dd", new Date());
}

const FULL_DATE_CLASSNAME = "react-datepicker__full" as const;

export type ReservationDatepickerProps = {
  date: Date | null;
  time: string | null;
  countryName: string;
  bookingSize?: number; // for first time reservations groupSize is not important, for rescheduling, it is
  setDate: (date: Date | null) => void;
  setTime: (time: string | null) => void;
  inlineCalendar?: boolean;
  canToggleGroupPrivacy?: boolean;
  isPrivate?: boolean;
  toggleIsPrivate?: () => void;
  minPrivateGroupSize?: number;
  maxPrivateGroupSize?: number;
} & Pick<
  ReturnType<typeof useAvailability>,
  | "getTimeSlots"
  | "availableDatesAsDate"
  | "isDateAvailable"
  | "isTimeSlotAvailable"
  | "getAvailableTimeSlots"
  | "getAvailableGroups"
  | "loading"
  | "loadedRange"
>;

const convertDateTimeZone = (date: Date | string) =>
  typeof date === "string" ? stringToDate(date) : new Date(date);

export default function ReservationDatepicker({
  getTimeSlots,
  availableDatesAsDate,
  isDateAvailable,
  isTimeSlotAvailable,
  date,
  time,
  setDate,
  setTime,
  getAvailableTimeSlots,
  inlineCalendar,
  canToggleGroupPrivacy,
  bookingSize = 0,
  isPrivate = false,
  toggleIsPrivate,
  getAvailableGroups,
  minPrivateGroupSize,
  maxPrivateGroupSize,
  loadedRange,
  loading,
  countryName,
}: ReservationDatepickerProps) {
  const parsedDate = useMemo(
    () => (date ? convertDateTimeZone(date) : null),
    [date]
  );
  const timeSlots = useMemo(() => {
    const slots = getTimeSlots(parsedDate) || [];
    return [...slots].sort((a, b) => a.time.localeCompare(b.time));
  }, [getTimeSlots, parsedDate]);

  const handleDatePickerChange = useCallback(
    (date: Date | null) => {
      setDate(convertDateTimeZone(date) || null);

      const availableTimeSlots = getAvailableTimeSlots(date);
      if (availableTimeSlots.length) setTime(availableTimeSlots[0].time);
    },
    [setDate, getAvailableTimeSlots, setTime]
  );

  const isDateInFuture = (date: Date) => date.getTime() >= Date.now();

  const getDayClassName = useCallback(
    (date) =>
      isDateInFuture(date) &&
      getTimeSlots(date).length &&
      !isDateAvailable(date)
        ? FULL_DATE_CLASSNAME
        : null,
    [isDateAvailable, getTimeSlots]
  );

  return (
    <>
      <>
        {canToggleGroupPrivacy && (
          <div style={{ display: "flex", flexDirection: "row", gap: 12 }}>
            <ToolTip
              text={
                "For an additional fee, you can opt for a private tour. Please note that the itinerary and content will match those of the regular group tour."
              }
            />
            <p>Make it a private tour</p>
            <Toggle toggled={isPrivate} onClick={toggleIsPrivate} />
          </div>
        )}
        {isPrivate && (
          <div style={{ color: "red", fontWeight: 500 }}>
            {bookingSize < minPrivateGroupSize && minPrivateGroupSize > 1
              ? `Private tours require at least ${minPrivateGroupSize} guests`
              : bookingSize > maxPrivateGroupSize
              ? `Up to ${maxPrivateGroupSize} guests may join a private tour`
              : ""}
          </div>
        )}
      </>
      <p>
        {inlineCalendar === false ? null : (
          <Text muted size="smaller">
            bookings accepted up to {MAX_BOOKING_MONTHS_IN_ADVANCE} months in
            advance
          </Text>
        )}
      </p>
      <div className="ReservationDatepicker">
        {inlineCalendar ? null : <p>Select Date: </p>}
        <LoadingCalendar
          loading={loading}
          inline={inlineCalendar ? inlineCalendar : false}
          onChange={handleDatePickerChange}
          selected={parsedDate}
          includeDates={availableDatesAsDate}
          dateFormat="MMM dd, yyyy"
          dayClassName={getDayClassName}
          from={new Date()}
          until={addMonths(new Date(), MAX_BOOKING_MONTHS_IN_ADVANCE)}
          loadedRange={loadedRange}
        />

        <p>
          <Text muted size="smaller">
            Dates are shown in{" "}
            <b className="ReservationDatepickerTimeZone">
              {countryName} Standard Time
            </b>
          </Text>
        </p>
      </div>
      <div className="ReservationDatepicker-InstantBooking">
        <InstantBooking />
      </div>
      <div className="ReservationDatepicker-TimePicker">
        {inlineCalendar ? null : <p>Select Time: </p>}

        {timeSlots.length > 0 && (
          <TimePicker.Select value={time} onChange={setTime}>
            {timeSlots.map((timeSlot) => {
              const { available, unavailableReason } = isTimeSlotAvailable(
                date,
                timeSlot.time
              );

              return (
                <TimePicker.Option
                  seats={Math.max(
                    ...getAvailableGroups(date, timeSlot.time).map(
                      (group) => group.seatsAvailable
                    ),
                    0
                  )}
                  key={timeSlot.time}
                  value={timeSlot.time}
                  disabled={!available}
                  disabledReason={unavailableReason}
                >
                  {timeSlot.time}
                </TimePicker.Option>
              );
            })}
          </TimePicker.Select>
        )}
      </div>
    </>
  );
}
